import { useEffect, useState } from 'react';
import useDebounce from 'hooks/useDebounce';
import { fetchAddress } from 'lib/address';
import { countryList as allCountries } from 'utils/constants/country';

/**
 * Address autocomplete state + handlers for the UserDetails form (HERE Maps).
 * Lifted verbatim from the former inline implementation in UserDetails.jsx.
 *
 * @param {object} formik - Formik bag (uses values, setFieldValue, validateForm)
 */
export function useAddressAutocomplete({ values, setFieldValue, validateForm }) {
    // country/state seed empty so the display falls back to getCountryNameByCode /
    // getStateNameByCode (full name) when values hold a code like 'US' / 'CA'.
    const [localValue, setLocalValue] = useState({
        country: '',
        address: values?.address || '',
        state: '',
    });
    const [addressList, setAddressList] = useState([]);
    const [isAddressLoading, setIsAddressLoading] = useState(false);

    // Only debounce the local address value, not the form value
    const debouncedAddress = useDebounce(localValue?.address, 350);

    // Watch for changes in debouncedAddress
    useEffect(() => {
        const fetchAddresses = async () => {
            setIsAddressLoading(true);
            // show suggestions only if address is more than 5 characters
            if (!debouncedAddress || debouncedAddress?.length < 5) {
                setAddressList([]);
                return;
            }

            if (values?.country) {
                let countryObj = allCountries.find(
                    (country) =>
                        country.label === values.country || country.value === values.country
                );

                const code = countryObj?.value;

                if (!code) return;

                try {
                    const result = await fetchAddress(code, debouncedAddress, {
                        show: 'houseNumber',
                    });
                    // Format address list for AutoComplete component (needs id and title properties)
                    const formattedAddressList = (result?.data?.items ?? [])
                        .slice(0, 5)
                        .map((item, index) => ({
                            id: index,
                            label: item.title || item.address?.label || '',
                            value: item,
                        }));
                    setAddressList(formattedAddressList);
                } catch {
                    setAddressList([]);
                } finally {
                    setIsAddressLoading(false);
                }
            }
        };

        fetchAddresses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedAddress, values?.country]);

    // Clear address list when country changes
    useEffect(() => {
        setAddressList([]);
        setLocalValue((prev) => ({ ...prev, address: '' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.country]);

    const resetAddressFields = () => {
        setFieldValue('state', '');
        setFieldValue('city', '');
        setFieldValue('houseNumber', '');
        setFieldValue('streetName', '');
        setFieldValue('zipCode', '');
    };

    const onSelectAddress = (data) => {
        if (!data) return;

        // Handle the new structure where the full item data is in the 'value' property
        const addressData = data.value || data;

        const {
            address: {
                postalCode: zipCode,
                city,
                stateCode,
                state: stateLabel,
                houseNumber,
                street,
            } = {},
        } = addressData;

        const addressLine1 = [houseNumber, street].filter(Boolean).join(', ');

        // 12345-678 👈 for this type of zip code we need to format it to 12345
        const formattedZipCode = zipCode?.includes('-') ? zipCode.split('-')[0] : zipCode;

        const addressUpdates = {
            address: addressLine1,
            zipCode: formattedZipCode,
            city,
            state: stateCode,
            houseNumber,
            streetName: street,
        };

        // update local state
        setLocalValue((prev) => ({
            ...prev,
            address: addressLine1,
            state: stateLabel,
        }));

        Object.entries(addressUpdates).forEach(([field, value]) => {
            setFieldValue(field, value ?? '');
        });

        // Force validation after state updates complete
        setTimeout(() => {
            validateForm();
        }, 0);
    };

    const handleDropdownValueChange = (e) => {
        const { name, value } = e.target;

        // Update form value
        setFieldValue(name, value);

        // Update local value for debouncing
        setLocalValue((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (name === 'country' && !value) {
            setLocalValue((prev) => ({
                ...prev,
                address: '',
                state: '',
            }));
            setAddressList([]);
        }
    };

    const handleDropdownValueSelect = (name, item) => {
        // Ignore invalid options. Writing an undefined value here deletes the whole
        // field from Formik (which removed `country` and crashed the State field).
        if (!name || !item || item.value == null) return;

        if (
            name === 'country' &&
            (localValue.country === item.label || localValue.country === item.value)
        )
            return;

        setFieldValue(name, item.value);
        setLocalValue((prev) => ({
            ...prev,
            [name]: item.label,
        }));

        // reset address fields when country changes
        if (name === 'country' && localValue.country !== item.label) {
            setFieldValue('address', '');
            resetAddressFields();
            setLocalValue({
                country: item.label,
                address: '',
                state: '',
            });
        }

        if (name === 'address') {
            resetAddressFields();
            onSelectAddress(item);
        }
    };

    return {
        localValue,
        addressList,
        isAddressLoading,
        handleDropdownValueChange,
        handleDropdownValueSelect,
    };
}
